import { useState } from 'react';
import { FolderDoc, FileDoc } from '../../types/forms-docs';
import { Folder, File, ChevronRight, ChevronDown } from 'lucide-react';

interface TreeViewProps {
  folder: FolderDoc;
  allFolders: FolderDoc[];
  onFileClick: (file: FileDoc) => void;
  level?: number;
}

export function TreeView({ folder, allFolders, onFileClick, level = 0 }: TreeViewProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const childFolders = allFolders
    .filter(f => f.parentId === folder.id)
    .sort((a, b) => a.id - b.id);

  const sortedFiles = [...folder.files].sort((a, b) => a.id - b.id);
  const hasChildren = childFolders.length > 0 || sortedFiles.length > 0;
  const paddingLeft = level * 20;

  return (
    <div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-full text-left flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-border transition-colors ${
          isExpanded ? 'bg-gray-50 dark:bg-dark-border' : ''
        }`}
        style={{ paddingLeft: `${paddingLeft}px` }}
      >
        {/* Always render a span for the arrow space to maintain alignment */}
        <span className="w-4 flex-shrink-0">
          {hasChildren && (
            <span className="text-gray-400">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </span>
          )}
        </span>
        <Folder className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
        <span className="text-gray-900 dark:text-gray-100 truncate">{folder.name}</span>
      </button>

      {isExpanded && (
        <div className="space-y-1 mt-1">
          {hasChildren ? (
            <>
              {childFolders.map(childFolder => (
                <TreeView
                  key={childFolder.id}
                  folder={childFolder}
                  allFolders={allFolders}
                  onFileClick={onFileClick}
                  level={level + 1}
                />
              ))}

              {sortedFiles.map(file => (
                <button
                  key={file.id}
                  onClick={() => onFileClick(file)}
                  className="w-full text-left flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-border transition-colors"
                  style={{ paddingLeft: `${paddingLeft + 26}px` }}
                >
                  <span className="w-4 flex-shrink-0" /> {/* Placeholder for alignment */}
                  <File className="h-5 w-5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300 truncate">
                    {file.name || file.filename}
                  </span>
                </button>
              ))}
            </>
          ) : (
            <div 
              className="text-sm text-gray-500 dark:text-gray-400 py-2 flex items-center"
              style={{ paddingLeft: `${paddingLeft + 26}px` }}
            >
              <span className="w-4 flex-shrink-0" /> {/* Placeholder for alignment */}
              This folder is empty
            </div>
          )}
        </div>
      )}
    </div>
  );
}